import React, {useContext, useState} from "react";
import {Avatar, Box, Card, Dialog, DialogContent, Typography, DialogTitle, IconButton} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import {OperationItem} from "../../components/layoutItems/mainItem";
import AddIcon from '@mui/icons-material/Add';
import AddSvg from "../../assets/wizard/operation/add.svg";

import {useTranslation} from "react-i18next";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {PRIVATE_URL} from "../../common/struct/urlManager";
import {AppContext} from "../../App";
import {OperationInformation, StoreContext, WizardType} from "../../common/struct/store";
import {LibertyTheme} from '../../tokens/libertyTheme';
import {initWizardStore} from "../../common/methods/context-setter/wizardStoreReducer";
import {PrimaryBigButton} from "../../components/buttons/mainButton";
import {sendEmailContact} from "../../services/EmailContactService";

export const OperationsList = (): JSX.Element => {
  const {t} = useTranslation();
  const STORE = useContext<StoreContext>(AppContext);
  const [store] = STORE;
  const location = useLocation();
  const [blockedOperationModalOpen, setBlockedOperationModalOpen] = useState(false);
  const navigate = useNavigate();
  const CLIENT_APPOINTMENT = "https://bookings.cloud.microsoft/book/AGENCELIBERTYRDVCLIENTS@tradeunion91.onmicrosoft.com/?ismsaljsauthenabled";

  const isOnOperationPages =
    location.pathname === PRIVATE_URL.CREATE_OPERATION_WIZARD ||
    location.pathname === PRIVATE_URL.LANDING_PAGE;

  const handleCreateClick = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    if (!store.global.company.operationCreationActivated && store.global.operations.length > 0) {
      setBlockedOperationModalOpen(true);
    } else {
      initWizardStore(STORE, WizardType.CREATE_OPERATION);
      navigate(PRIVATE_URL.CREATE_OPERATION_WIZARD);
    }
  };

  return (
    <Box id="operation-list" sx={{display: "flex", flexDirection: "column", height: "100%", pb: 5, boxSizing: "border-box"}}>
      <Typography color="secondaryLight.main" variant="caption" sx={{height: "fit-content"}}>
        {t("layout_sideBar.operation_title").toUpperCase()}
      </Typography>
      <Link to={PRIVATE_URL.CREATE_OPERATION_WIZARD} onClick={handleCreateClick} style={{height: "fit-content"}}>
        <Card sx={{
          display: "flex", alignItems: "center", p: 1, mt: 2, mb: 1, boxShadow: 1, cursor: "pointer",
          backgroundColor: isOnOperationPages ? "secondary.main" : "transparent", borderColor: isOnOperationPages ? "secondary.main" : "secondary.light",
          ":hover": {borderColor: "secondaryLight.dark", "& p": {color: "ornament.light"}}
        }}>
          <Avatar sx={{backgroundColor: "primary.main", p: "2px", mr: 1, width: 20, height: 20}}>
            <AddIcon sx={{color: "ornament.light"}} />
          </Avatar>
          <Typography variant="body2" color={isOnOperationPages ? "ornament.light" : "secondaryLight.light"} sx={{fontWeight: "medium"}}>
            {t("layout_sideBar.new_operation_label")}
          </Typography>
        </Card>
      </Link>

      <Dialog open={blockedOperationModalOpen} onClose={(): void => setBlockedOperationModalOpen(false)} sx={{"& .MuiDialog-container": {height: "100%", "& canvas": {position: "fixed !important"}, "& .MuiDialog-paper": {maxWidth: "562px"}}}}>
        <DialogTitle sx={{display: "flex", justifyContent: "flex-end", backgroundColor: "primaryLight.light", pt: 5, pr: 5, pb: "0px"}}>
          <IconButton sx={{p: "0px"}} onClick={(): void => setBlockedOperationModalOpen(false)}><ClearIcon sx={{color: "neutral.main"}} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{p: "0px"}}>
          <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", backgroundColor: "primaryLight.light", px: 5, pb: 8}}>
            {/** @ts-ignore */}
            <img src={AddSvg} alt="" />
            <Typography variant="h1" sx={{fontWeight: "bold", textAlign: "center", mt: 2}}>
              {t("operations.blocked_operation.title")}
            </Typography>
          </Box>
          <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", p: 5, pb: 6}}>
            <Typography variant="body2" color="neutral.main" sx={{mb: 2, textAlign: "center", "& b": {fontWeight: "bold"}}} dangerouslySetInnerHTML={{__html: t("operations.blocked_operation.content")}} />
            <div
              onClick={async (): Promise<void> => {
                sendEmailContact({askContact: true});
                window.open(CLIENT_APPOINTMENT, "_blank");
              }}
              style={{width: "100%", display: "flex", justifyContent: "center"}}
            >
              <PrimaryBigButton
                label={t("operations.blocked_operation.button_label")}
                sx={{mt: 2}}
              />
            </div>
          </Box>
        </DialogContent>
      </Dialog>
      <Box sx={{overflowY: "auto", width: "100%", maxHeight: "300px", pr: 0, "&::-webkit-scrollbar": {width: "6px"}, "&::-webkit-scrollbar-thumb": {background: LibertyTheme.palette.secondaryLight.main, borderRadius: "8px"}}}>
        {store.global.operations.map((operation: OperationInformation) => {
          const index = store.global.operations.indexOf(operation);
          return <OperationItem
            key={"operation-" + index}
            label={operation.name}
            opId={operation.id}
            index={index}
            isClicked_val={location.pathname.includes(operation.id)} />
        })}
      </Box>
    </Box>
  )
}
